import { Modal, Checkbox, Space, Typography, Button, Tooltip } from "antd";
import React, { useContext, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateApply1DsMappingModalDisplay } from "@features/modalSlice";
import { map, uniq } from "underscore";
import {
    GlobalTagRecordType,
    IMainContext,
    IPfmSkuFilterParams,
    TClientTags,
    TProductMappingData,
    VcpmColumns,
} from "@/ts/common-types";
import { apply1DSMapping, internationalizeNumericValue } from "@/ts/utils";
import { InfoCircleOutlined } from "@ant-design/icons";
import { mainContext } from "@/ts/constants";
import { setIsAddTagTypeFormSubmitted } from "@/features/clientDataSlice";
import { showToast } from "@/ts/modals";

const { Text, Title } = Typography;

type IApply1DSMappingModalProps = {
    show: boolean;
};

const Apply1DSMappingModal: React.FC<IApply1DSMappingModalProps> = ({
    show,
}) => {
    const { productMappingGridApi } = useContext<IMainContext>(mainContext);

    const dispatch = useDispatch();
    const [selectedParams, setSelectedParams] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const bulkPfmMappingCount = useSelector(
        (state: { bulkPfmMappingCount: { value: number } }) =>
            state.bulkPfmMappingCount.value
    );

    const productMappingSelectionData = useSelector(
        (state: TProductMappingData) => state.productMappingSelectionData.value
    );

    const productMappingFilterModel = useSelector(
        (state: {
            productMappingFilterModel: { value: IPfmSkuFilterParams };
        }) => state.productMappingFilterModel.value
    );

    const tagTypeList = useSelector(
        (state: { tagTypeList: { value: TClientTags[] } }) =>
            state.tagTypeList.value
    );

    const productMappingAllPagesSelected = useSelector(
        (state: { productMappingAllPagesSelected: { value: boolean } }) =>
            state.productMappingAllPagesSelected.value
    );

    const mappingParameters = [
        { id: VcpmColumns.CATEGORY_1DS, label: "1DS Category" },
        { id: VcpmColumns.SUBCATEGORY_1DS, label: "1DS Subcategory" },
        { id: VcpmColumns.BRAND_1DS, label: "1DS Brand" },
        { id: VcpmColumns.PACK_SIZE_BAND, label: "Pack Size Band" },
        { id: VcpmColumns.PRICE_BAND, label: "Price Band" },
    ];

    const tagFields = [VcpmColumns.PACK_SIZE_BAND, VcpmColumns.PRICE_BAND];

    const cleanedTagColFields = tagFields
        .map((field) => field.replace(/[^a-zA-Z]/g, "").toLowerCase())
        .filter((_, index) => selectedParams.includes(tagFields[index]));

    const cleanedSkuTagTypeList = useMemo(() => {
        return map(
            tagTypeList.filter(
                (tag) => tag.record_type === GlobalTagRecordType.PRODUCT
            ),
            (tag) => tag.tag_type.toLowerCase()
        );
    }, [tagTypeList]);

    const skuTagTypeList = uniq([
        ...cleanedSkuTagTypeList,
        ...cleanedTagColFields,
    ]);

    const handleParameterChange = (checkedValues: string[]) => {
        setSelectedParams(checkedValues);
    };

    const handleSubmit = async () => {
        try {
            if (skuTagTypeList.length > 10) {
                throw new Error("Tag type limit exceeded!");
            }

            setIsSubmitting(true);

            const applyMappingConfig = {
                ...(productMappingSelectionData.length && !productMappingAllPagesSelected
                    ? { 
                        skuChannel: productMappingSelectionData.map((data) => ({
                            channel: data.channel_id as number,
                            sku: data.sku as string | number,
                        }))
                      }
                    : { 
                        skuChannel: [],
                        filterModel: productMappingFilterModel 
                      }
                ),
                updateParams: selectedParams,
            };

            const response = await apply1DSMapping(applyMappingConfig);

            if (!response.status) {
                throw new Error(response.message);
            }

            // Refresh the grid data
            if (productMappingGridApi) {
                productMappingGridApi.refreshServerSideStore({
                    route: [],
                    purge: true,
                });
            }

            //to refresh the tag types list
            dispatch(setIsAddTagTypeFormSubmitted(true));

            dispatch(updateApply1DsMappingModalDisplay(false));
        } catch (error) {
            console.error("Error applying mapping:", error);
            showToast(
                "danger",
                error instanceof Error
                    ? error.message
                    : "An error occurred while applying the mapping."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        dispatch(updateApply1DsMappingModalDisplay(false));
    };

    return (
        <Modal
            centered
            title={
                <Space
                    direction="vertical"
                    size={8}
                    style={{ width: "100%", paddingRight: "24px" }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                        }}
                    >
                        <Title level={4} style={{ margin: 0 }}>
                            Apply 1DS Mapping
                        </Title>
                        <Tooltip title="The mappings will only be applied to those skus that don't have any value mapped already in the corresponding parameter's column.">
                            <InfoCircleOutlined
                                style={{ color: "#8c8c8c", fontSize: "18px" }}
                            />
                        </Tooltip>
                    </div>
                    <div
                        className="d-flex align-items-center"
                        style={{
                            fontSize: "0.8rem",
                            color: "#666",
                            fontWeight: "400",
                            backgroundColor: "#f5f5f5",
                            padding: "4px 12px",
                            borderRadius: "4px",
                            width: "fit-content",
                            marginTop: "4px",
                        }}
                    >
                        Filtered SKU Count:{" "}
                        <span style={{ marginLeft: "6px", fontWeight: "600" }}>
                            {internationalizeNumericValue(
                                productMappingAllPagesSelected
                                    ? bulkPfmMappingCount
                                    : productMappingSelectionData.length || bulkPfmMappingCount
                            )}
                        </span>
                    </div>
                </Space>
            }
            open={show}
            onCancel={handleCancel}
            width={400}
            styles={{
                body: {
                    padding: "24px",
                },
            }}
            footer={[
                <Button
                    key="cancel"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={selectedParams.length === 0 || isSubmitting}
                >
                    Submit
                </Button>,
            ]}
        >
            <Checkbox.Group
                onChange={handleParameterChange}
                value={selectedParams}
                style={{ width: "100%" }}
            >
                <Space
                    direction="vertical"
                    style={{ width: "100%" }}
                    size="middle"
                >
                    {map(mappingParameters, (param) => (
                        <div
                            key={param.id}
                            style={{
                                padding: "12px 16px",
                                borderRadius: "6px",
                                backgroundColor: selectedParams.includes(
                                    param.id
                                )
                                    ? "#e6f7ff"
                                    : "#fafafa",
                                border: selectedParams.includes(param.id)
                                    ? "1px solid #1890ff"
                                    : "1px solid #f0f0f0",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                boxShadow: selectedParams.includes(param.id)
                                    ? "0 2px 8px rgba(24, 144, 255, 0.15)"
                                    : "0 1px 2px rgba(0, 0, 0, 0.05)",
                                display: "flex",
                                alignItems: "center",
                                maxWidth: "300px",
                                margin: "0 auto",
                            }}
                            onMouseEnter={(e) => {
                                if (!selectedParams.includes(param.id)) {
                                    e.currentTarget.style.borderColor =
                                        "#1890ff";
                                    e.currentTarget.style.boxShadow =
                                        "0 2px 8px rgba(24, 144, 255, 0.15)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!selectedParams.includes(param.id)) {
                                    e.currentTarget.style.borderColor =
                                        "#f0f0f0";
                                    e.currentTarget.style.boxShadow =
                                        "0 1px 2px rgba(0, 0, 0, 0.05)";
                                }
                            }}
                            onClick={() =>
                                setSelectedParams(
                                    selectedParams.includes(param.id)
                                        ? selectedParams.filter(
                                            (id) => id !== param.id
                                        )
                                        : [...selectedParams, param.id]
                                )
                            }
                        >
                            <Checkbox value={param.id}>
                                <Text style={{ fontSize: "14px" }}>
                                    {param.label}
                                </Text>
                            </Checkbox>
                        </div>
                    ))}
                </Space>
            </Checkbox.Group>
        </Modal>
    );
};

export default Apply1DSMappingModal;
